// Regras puras do calendário de atividades (09/10/2026) — a grade da semana
// com as horas do dia, a mesma da anotação de horas (pedido do Lucas). Ficam
// aqui, testadas, e o componente só desenha.
//
// 🚨 A data da atividade é PAREDE de Brasília: o banco guarda TIMESTAMP sem
// fuso e a API devolve "2026-10-13T14:00:00.000Z" — o "Z" mente. Ler com
// `new Date()` desloca 3 h e joga meia-noite no dia ANTERIOR. Aqui a data e a
// hora saem do texto, nunca do relógio do computador.
import { layoutDia, segundaDeIso, somarDias } from '../operacional-horas/horasUtils';
import type { Atividade } from './types';

/** Duração com que um compromisso aparece na grade (atividade não tem fim):
 *  45 min dão altura para a segunda linha, a do cliente. */
export const DURACAO_PADRAO_MIN = 45;

export type Quando = { dia: string; minuto: number | null };

/** Dia e minuto da atividade. Meia-noite e 23:59 são PRAZO do dia (sem hora
 *  marcada): o follow-up automático grava 23:59 e a tela antiga, 00:00. */
export function quandoDe(iso: string | null | undefined): Quando | null {
  const m = String(iso || '').match(/^(\d{4}-\d{2}-\d{2})(?:[T ](\d{2}):(\d{2}))?/);
  if (!m) return null;
  if (!m[2]) return { dia: m[1], minuto: null };
  const minuto = Number(m[2]) * 60 + Number(m[3]);
  return { dia: m[1], minuto: minuto === 0 || minuto === 23 * 60 + 59 ? null : minuto };
}

/** "Agora" em São Paulo, pelo relógio do computador convertido para o fuso de
 *  lá (a tela é de Brasília; o computador pode estar em outro fuso). */
export function agoraSaoPaulo(d: Date = new Date()): { dia: string; minuto: number } {
  const partes = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(d);
  const v = (t: string) => partes.find(p => p.type === t)?.value || '00';
  return { dia: `${v('year')}-${v('month')}-${v('day')}`, minuto: Number(v('hour')) * 60 + Number(v('minute')) };
}

export type Visao = 'dia' | '3' | '7';
export const diasDaVisao = (v: Visao) => (v === '7' ? 7 : v === '3' ? 3 : 1);

/** Primeiro dia mostrado: a segunda-feira na semana; o próprio dia nas outras. */
export const inicioDaVisao = (dia: string, v: Visao) => (v === '7' ? segundaDeIso(dia) : dia);

/** Os dias visíveis a partir do primeiro. */
export const diasVisiveis = (inicio: string, v: Visao) =>
  Array.from({ length: diasDaVisao(v) }, (_, i) => somarDias(inicio, i));

/** Andar para a frente ou para trás o tamanho da visão. */
export const andar = (inicio: string, v: Visao, sentido: 1 | -1) => somarDias(inicio, sentido * diasDaVisao(v));

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
/** "6 – 12 out 2026", "28 set – 4 out 2026", "9 out 2026". */
export function rotuloPeriodo(dias: string[]): string {
  if (!dias.length) return '';
  const p = (iso: string) => { const [y, m, d] = iso.split('-').map(Number); return { y, m, d }; };
  const a = p(dias[0]); const b = p(dias[dias.length - 1]);
  if (dias.length === 1) return `${a.d} ${MESES[a.m - 1]} ${a.y}`;
  if (a.y !== b.y) return `${a.d} ${MESES[a.m - 1]} ${a.y} – ${b.d} ${MESES[b.m - 1]} ${b.y}`;
  if (a.m !== b.m) return `${a.d} ${MESES[a.m - 1]} – ${b.d} ${MESES[b.m - 1]} ${b.y}`;
  return `${a.d} – ${b.d} ${MESES[b.m - 1]} ${b.y}`;
}

export type BlocoAtividade = { id: string; inicio: number; fim: number; a: Atividade; coluna: number; colunas: number };
export type DiaDoCalendario = { diaTodo: Atividade[]; blocos: BlocoAtividade[]; total: number };

/** Separa as atividades: as SEM DATA (não somem — ficam numa faixa própria),
 *  e, em cada dia visível, as do dia todo e os blocos com hora já arrumados
 *  lado a lado quando se sobrepõem. */
export function montarCalendario(atividades: Atividade[], dias: string[]): {
  semData: Atividade[]; porDia: Map<string, DiaDoCalendario>;
} {
  const semData: Atividade[] = [];
  const porDia = new Map<string, DiaDoCalendario>();
  const comHora = new Map<string, Array<{ id: string; inicio: number; fim: number; a: Atividade }>>();
  dias.forEach(d => { porDia.set(d, { diaTodo: [], blocos: [], total: 0 }); comHora.set(d, []); });
  for (const a of atividades) {
    const q = quandoDe(a.data_vencimento);
    if (!q) { semData.push(a); continue; }
    const dia = porDia.get(q.dia);
    if (!dia) continue;
    dia.total++;
    if (q.minuto == null) dia.diaTodo.push(a);
    else comHora.get(q.dia)!.push({ id: a.id, inicio: q.minuto, fim: Math.min(1440, q.minuto + DURACAO_PADRAO_MIN), a });
  }
  comHora.forEach((lista, d) => { porDia.get(d)!.blocos = layoutDia(lista) as BlocoAtividade[]; });
  return { semData, porDia };
}

/** Atrasada: tem data, já passou e ainda está aberta. Prazo do dia vence no
 *  fim do dia; compromisso com hora, na hora. */
export function atrasada(a: Atividade, agora: { dia: string; minuto: number }): boolean {
  if (a.status === 'concluida' || a.status === 'cancelada') return false;
  const q = quandoDe(a.data_vencimento);
  if (!q) return false;
  if (q.dia !== agora.dia) return q.dia < agora.dia;
  return q.minuto != null && q.minuto < agora.minuto;
}

/** Clique num horário vazio da grade → a data/hora da atividade nova, no
 *  formato do campo da janela ('AAAA-MM-DDTHH:MM'), encaixada de 15 em 15. */
export function dataHoraDoClique(dia: string, minuto: number): string {
  const m = Math.max(0, Math.min(1425, Math.floor(minuto / 15) * 15));
  return `${dia}T${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

/** "Negócio · Cliente" de uma atividade do comercial (ou só um dos dois). */
export function oportunidadeECliente(a: Pick<Atividade, 'negocio_titulo' | 'cliente_nome'>): string {
  return [a.negocio_titulo, a.cliente_nome].filter(Boolean).join(' · ');
}

/** O mesmo, com o CLIENTE na frente — para o espaço curto do calendário. */
export function clienteEOportunidade(a: Pick<Atividade, 'negocio_titulo' | 'cliente_nome'>): string {
  return [a.cliente_nome, a.negocio_titulo].filter(Boolean).join(' · ');
}

/** Rótulo do prazo da lista ("Atrasada (2d)", "Hoje", "Amanhã", "Em N dias",
 *  ou a data), contado em DIAS de Brasília a partir da data como ela está
 *  gravada. 🚨 Antes era `new Date(iso)`: o "Z" fazia a meia-noite cair às
 *  21h do dia anterior e "depois de amanhã" aparecia como "Amanhã". */
export function rotuloDoPrazo(iso: string, hoje: string): { texto: string; diff: number } | null {
  const q = quandoDe(iso);
  if (!q) return null;
  const ms = (dia: string) => { const [y, m, d] = dia.split('-').map(Number); return Date.UTC(y, m - 1, d); };
  const diff = Math.round((ms(q.dia) - ms(hoje)) / 86400000);
  const data = `${q.dia.slice(8, 10)}/${q.dia.slice(5, 7)}/${q.dia.slice(0, 4)}`;
  const texto = diff < 0 ? `Atrasada (${-diff}d)` : diff === 0 ? 'Hoje' : diff === 1 ? 'Amanhã' : diff < 7 ? `Em ${diff} dias` : data;
  return { texto, diff };
}
